import React, { useMemo, useState } from 'react';
import { Link } from '../link';
import './MainHeader.scss';
import NavigationBusiness from './NavigationBusiness';
import { Wrapper } from '../../utils/components';
import LanguageSelector from './LanguageSelector';
import slugify from 'slugify';
import classNames from 'classnames';
import MainHeaderIcon from './MainHeaderIcon';
import SocialIconsMenu from '../../molecules/social-icon/SocialIconsMenu';
import Loadable from '@loadable/component';
import useBlockScroll from '../../utils/hooks/useBlockScroll';
import LogoIcon from '../../atoms/static-icons/cattolica-logo';
import Lens from '../../atoms/static-icons/lens';
import MapLocatorWithLogo from '../../atoms/static-icons/map-locator-with-logo';
import CattolicaAngel from '../../atoms/static-icons/cattolica-angel';

const LoadableSearch = Loadable(() => import('../search/components/Search'));

function MainHeader(
  {
    pageType,
    disableLinkLogo,
    langCode,
    hasScrolled,
    linkLangMap,
    link,
    showChangeLanguageButton,
    hideSocialButton,
    lateralStickerAnchor,
    menuTopHeaderBusiness,
  },
  navigationMenuRef
) {
  const HeaderIconWrapper = link && link === '/' ? 'h1' : 'div';

  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);

  const menuLeftMobile =
    menuTopHeaderBusiness?.leftSectionBusiness?.content?.labelLinkBusinessLeft.slice(
      1
    );
  const menuRightMobile =
    menuTopHeaderBusiness?.rightSectionBusiness?.content?.labelLinkBusinessRight.slice(
      0,
      -1
    );

  const linkLeft = useMemo(
    () =>
      menuLeftMobile?.filter(
        (item) =>
          (item?.value ||
            item.content?.linkExternalBusinessLeft?.value ||
            item.content?.linkInternalBusinessLeft?.value?.link) &&
          !item.content?.linkNotVisibleBusinessLeft?.value
      ),
    [menuLeftMobile]
  );
  const linkRight = useMemo(
    () =>
      menuRightMobile?.filter(
        (item) =>
          (item?.value ||
            item.content?.linkExternalBusinessRight?.value ||
            item.content?.linkInternalBusinessRight?.value?.link) &&
          !item.content?.linkNotVisibleBusinessRight?.value
      ),
    [menuRightMobile]
  );

  useBlockScroll(isSearchOpen); // Call hook to hide scroll page

  return (
    <div className='main-header wrapper' data-submodule-name='main-header'>
      <HeaderIconWrapper
        className={classNames('main-header__logo', {
          'main-header__logo--landing': pageType === 'landing',
        })}
      >
        <Wrapper
          aria-label='homepage'
          condition={!disableLinkLogo}
          component={Link}
          className='main-header__logo--link'
          to='/'
        >
          <LogoIcon />
        </Wrapper>
      </HeaderIconWrapper>
      {pageType === 'business' && (
        <HeaderIconWrapper className='main-header__angel'>
          <Wrapper
            aria-label='home page'
            condition={!disableLinkLogo}
            component={Link}
            className='main-header__angel--link'
            to='/'
          >
            <CattolicaAngel aria-label='Logo Cattolica' aria-hidden={true} />
          </Wrapper>
        </HeaderIconWrapper>
      )}
      {pageType === 'business' && (
        <NavigationBusiness
          ref={navigationMenuRef}
          langCode={langCode}
          openBurger={megaMenuOpen}
          setSearchOpen={setSearchOpen}
        />
      )}
      {pageType === 'business' && (
        <section className='main-header__tools'>
          {linkLeft?.map(
            (item) =>
              (item?.value ||
                item.content.iconSvgIdBusinessLeft?.value ||
                item.content.linkInternalBusinessLeft?.value?.pageTitle
                  ?.value ||
                item.content.linkExternalBusinessLeft.value) && (
                <MainHeaderIcon
                  labelItem={item.value}
                  iconItem={item.content.iconSvgIdBusinessLeft.value}
                  pageTitleItem={
                    item.content.linkInternalBusinessLeft?.value?.pageTitle
                      ?.value
                  }
                  internalLinkItem={
                    item.content.linkInternalBusinessLeft?.value?.link
                  }
                  externalLinkItem={item.content.linkExternalBusinessLeft.value}
                  key={item.value}
                />
              )
          )}
          {linkRight?.map(
            (item) =>
              (item?.value ||
                item.content.iconSvgIdBusinessRight?.value ||
                item.content.linkInternalBusinessRight?.value?.pageTitle
                  ?.value ||
                item.content.linkExternalBusinessRight.value) && (
                <MainHeaderIcon
                  labelItem={item.value}
                  iconItem={item.content.iconSvgIdBusinessRight.value}
                  pageTitleItem={
                    item.content.linkInternalBusinessRight?.value?.pageTitle
                      ?.value
                  }
                  internalLinkItem={
                    item.content.linkInternalBusinessRight?.value?.link
                  }
                  externalLinkItem={
                    item.content.linkExternalBusinessRight.value
                  }
                  key={item.value}
                />
              )
          )}
          {lateralStickerAnchor && (
            <Link
              aria-label='Trova agenzia'
              className='main-header__item main-header__item--mobile'
              to={`#${slugify(lateralStickerAnchor, {
                lower: true,
                strict: true,
              })}`}
            >
              <span className='main-header__svg'>
                <MapLocatorWithLogo />
              </span>
            </Link>
          )}
          <button
            aria-label='ricerca'
            className={`main-header__item main-header__search-button${
              isSearchOpen ? ' search-open' : ''
            }`}
            onClick={() => {
              setSearchOpen(!isSearchOpen);
              setMegaMenuOpen(false);
            }}
          >
            <span className='main-header__svg'>
              <Lens type='stroked' thickness='normal' />
            </span>
          </button>
          {isSearchOpen && (
            <LoadableSearch
              onClose={() => {
                setSearchOpen(false);
              }}
              hasScrolled={hasScrolled}
            />
          )}
          <button
            aria-label='apri menu'
            onClick={() => {
              setMegaMenuOpen(!megaMenuOpen);
              setSearchOpen(false);
            }}
            className={classNames('main-header__hamburger', {
              'main-header__hamburger--open': megaMenuOpen,
            })}
          >
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </section>
      )}
      {pageType === 'landing' && (
        <section className='main-header__tools'>
          {!hideSocialButton && <SocialIconsMenu />}
          {!hideSocialButton && showChangeLanguageButton && (
            <span className='main-header__separator'></span>
          )}
          {showChangeLanguageButton && (
            <LanguageSelector
              linkLangMap={linkLangMap}
              langCode={langCode}
              className={'main-header__language'}
            />
          )}
        </section>
      )}
    </div>
  );
}

export default React.forwardRef(MainHeader);
